/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useState } from 'react';
import {
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogOpenChangeData,
    DialogSurface,
    DialogTitle,
    DialogTrigger,
    Field,
    Input,
    Textarea,
    ToggleButton,
    makeStyles,
    mergeClasses,
    tokens,
} from '@fluentui/react-components';
import { bundleIcon, MoneyFilled, MoneyRegular, TaskListAddRegular } from '@fluentui/react-icons';
import { Column, Row } from '../common';
import { useAccountState } from '../../redux/reducers/accountReducer';
import { TaskTemplate, useInitialValues } from '../../data/types';
import { useAccess } from '../../auth/useAccess';
import { useCommonStyles } from '../common/Styles/commonStyles';
import { useFormik } from 'formik';
import { useValidation } from '../../data/types/validation';
import { useDialogControls } from './useDialogControls';

const useStyles = makeStyles({
    field: {
        width: '100%',
    },
    dialog: {
        border: `1px solid ${tokens.colorBrandStroke1}`,
        maxWidth: '600px',
        transitionDuration: '.5s',
    },
    billableBtn: {
        ':disabled': {
            color: '#333',
        },
    },
});
export type AddTaskTemplateDialogProps = {
    onSubmit?: (task: TaskTemplate) => Promise<void>;
    children?: JSX.Element;
    taskToEdit?: TaskTemplate;
    open?: boolean;
    onOpenChange?: (isOpen: boolean) => void;
};
export const AddTaskTemplateDialog = (props: AddTaskTemplateDialogProps) => {
    const { children, onSubmit, taskToEdit, open, onOpenChange } = props;

    //Hooks
    const { primaryAccount } = useAccountState();
    const access = useAccess(primaryAccount?.user);
    const initValues = useInitialValues();
    const { validateTaskTemplate } = useValidation();

    //State
    // const [isOpen, setIsOpen] = useState<boolean>(open);

    //Styles
    const commonStyles = useCommonStyles();
    const styles = useStyles();
    const dialogClasses = mergeClasses(styles.dialog);
    const billableBtnClasses = mergeClasses(commonStyles.button.root, styles.billableBtn);

    //Form
    const initTaskTemplate = (): TaskTemplate => {
        if (taskToEdit) {
            return {
                ...initValues.taskTemplate(),
                ...taskToEdit,
            };
        } else {
            return {
                ...initValues.taskTemplate(),
            };
        }
    };
    const taskForm = useFormik<TaskTemplate>({
        initialValues: initTaskTemplate(),
        onSubmit: async (values, { resetForm }) => {
            const task = values as TaskTemplate;

            if (!!task.title && !!task.text) {
                !!onSubmit && (await onSubmit(task));

                resetForm({ values: initTaskTemplate() });
            }
        },
        validationSchema: validateTaskTemplate.schema,
    });

    //Dialog Controls
    const [dialogOpen, onOpenChangeHandler] = useDialogControls(open, onOpenChange);

    //Logic
    const resetForm = () => {
        taskForm.resetForm({ values: { ...initTaskTemplate() } });
    };
    const clickBillable = (e: any) => {
        e.stopPropagation();
        taskForm.setFieldValue('billable', !taskForm.values.billable);
    };

    //Effects
    useEffect(() => {
        if (!!dialogOpen && !!taskToEdit) {
            resetForm();
        }
    }, [dialogOpen, taskToEdit]);

    //Icons
    const MoneyIcons = bundleIcon(MoneyFilled, MoneyRegular);

    return (
        <Dialog open={dialogOpen} onOpenChange={onOpenChangeHandler}>
            <DialogTrigger disableButtonEnhancement>{children}</DialogTrigger>
            <DialogSurface aria-describedby={undefined} mountNode={undefined} className={dialogClasses}>
                <DialogBody>
                    <DialogTitle>
                        <Row gap='medium'>
                            <Column width='auto' vAlign='top'>
                                <TaskListAddRegular />
                            </Column>
                            <Column align='left' vAlign='top'>
                                Add/Edit Task in Milestone
                            </Column>
                        </Row>
                    </DialogTitle>
                    <DialogContent>
                        <Column>
                            <Row>
                                <Column gap='medium'>
                                    <Column align='right' vAlign='top'>
                                        <ToggleButton
                                            className={billableBtnClasses}
                                            name='billable'
                                            appearance={taskForm.values.billable ? 'primary' : 'secondary'}
                                            checked={taskForm.values.billable}
                                            onChange={taskForm.handleChange}
                                            onClick={clickBillable}
                                            icon={<MoneyIcons />}
                                            size='small'
                                            disabled={!access.cases.canEditTasks()}
                                            data-testid='task-template-dialog-billable-button'
                                        >
                                            Billable
                                            <Checkbox
                                                name='billable'
                                                className={commonStyles.button.root}
                                                labelPosition='before'
                                                checked={taskForm.values.billable}
                                                onChange={taskForm.handleChange}
                                                readOnly={!access.cases.canEditTasks()}
                                            />
                                        </ToggleButton>
                                    </Column>
                                    <Field
                                        className={styles.field}
                                        label='Title'
                                        required
                                        validationMessage={taskForm.errors.title}
                                    >
                                        <Input
                                            name='title'
                                            tabIndex={3}
                                            value={taskForm.values.title}
                                            onChange={taskForm.handleChange}
                                            readOnly={!access.cases.canEditTasks()}
                                            appearance={!access.cases.canEditTasks() ? 'outline' : undefined}
                                            maxLength={validateTaskTemplate.text.maxLength}
                                            data-testid='task-template-dialog-title'
                                        />
                                    </Field>
                                    <Field
                                        className={styles.field}
                                        label='Description'
                                        required
                                        validationMessage={taskForm.errors.text}
                                        hint={`${taskForm.values.text?.length ?? 0} / ${validateTaskTemplate.text.maxLength}`}
                                    >
                                        <Textarea
                                            name='text'
                                            tabIndex={3}
                                            rows={!!taskToEdit ? 7 : 4}
                                            value={taskForm.values.text}
                                            onChange={taskForm.handleChange}
                                            readOnly={!access.cases.canEditTasks()}
                                            appearance={!access.cases.canEditTasks() ? 'outline' : undefined}
                                            maxLength={validateTaskTemplate.text.maxLength}
                                            data-testid='task-template-dialog-textarea'
                                        />
                                    </Field>
                                </Column>
                            </Row>
                        </Column>
                    </DialogContent>
                    <DialogActions fluid={true}>
                        {(access.cases.canCreateTasks() || (taskToEdit && access.cases.canEditTasks())) && (
                            <DialogTrigger disableButtonEnhancement>
                                <Button
                                    className={commonStyles.button.root}
                                    type='submit'
                                    appearance='primary'
                                    disabled={!taskForm.dirty || !taskForm.isValid}
                                    onClick={() => taskForm.submitForm()}
                                    data-testid='task-template-dialog-save-button'
                                >
                                    Save
                                </Button>
                            </DialogTrigger>
                        )}
                        <DialogTrigger disableButtonEnhancement>
                            <Button
                                className={commonStyles.button.root}
                                appearance='secondary'
                                onClick={resetForm}
                                data-testid='task-template-dialog-cancel-button'
                            >
                                {(access.cases.canCreateTasks() || (taskToEdit && access.cases.canEditTasks())) &&
                                taskForm.dirty
                                    ? 'Cancel'
                                    : 'Close'}
                            </Button>
                        </DialogTrigger>
                    </DialogActions>
                </DialogBody>
            </DialogSurface>
        </Dialog>
    );
};
